{
    This file is part of the Fresnel Library.
    Copyright (c) 2026 by the FPC & Lazarus teams.

    Webassembly rendering - common consts for Fresnel

    See the file COPYING.modifiedLGPL.txt, included in this distribution,
    for details about the copyright.

    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

 **********************************************************************}

unit Fresnel.Wasm.Shared;

{$mode objfpc}{$H+}
{$modeswitch typehelpers}
{$modeswitch advancedrecords}
{$IFNDEF PAS2JS}
{$modeswitch functionreferences}
{$ENDIF}

interface

uses
  {$IFDEF FPC_DOTTEDUNITS}
  System.Classes, System.SysUtils;
  {$ELSE}
  Classes, SysUtils;
  {$ENDIF}

const
  WindowMsgSize = 4;
  CanvasMeasureTextSize = 6;
  FresnelScaleFactor = 100;

  // Delayed pointer processing actions
  puaFile = 0;

type
  {$IFNDEF PAS2JS}
  TFresnelFloat = single;
  {$ELSE}
  TFresnelFloat = double;
  {$ENDIF}
  TFresnelFloatArray = array of TFresnelFloat;

  TCanvasError = longint;

  { TWindowCanvasID }

  // ID for a Canvas that exists in the DOM and corresponds to a Window
  {$ifdef PAS2JS}
  // using Int16 here to work around type helper limitation
  // (type helper is bound to the unaliased type, so we need a different type than for TOffscreenCanvasID)
  TWindowCanvasID = Int16;
  {$else}
  TWindowCanvasID = record
    Win: longint;
  end;
  {$endif}

  TWindowCanvasIDHelper = {$ifdef PAS2JS}type{$else}record{$endif} helper for TWindowCanvasID
    function ToIDString: string; inline;
    function ToString: string;
  end;

  { TOffscreenCanvasID }

  // ID for an OffscreenCanvas that can exist in main thread or webworker
  {$ifdef PAS2JS}
  TOffscreenCanvasID = Int32;
  {$else}
  TOffscreenCanvasID = record
    Cnv: longint;
  end;
  {$endif}

  TOffscreenCanvasIDHelper = {$ifdef PAS2JS}type{$else}record{$endif} helper for TOffscreenCanvasID
    function ToIDString: string; inline;
    function ToString: string;
  end;

  { TVideoElementID }
  // ID for an OffscreenCanvas that can exist in main thread or webworker
  {$ifdef PAS2JS}
  TVideoElementID = Int32;
  {$else}
  TVideoElementID = record
    Vid: longint;
  end;
  {$endif}

  TVideoElementIDHelper = {$ifdef PAS2JS}type{$else}record{$endif} helper for TVideoElementID
    function ToIDString: string; inline;
    function ToString: string;
  end;

const
  // not the default value for ID, but the ID of the default canvas
  // which is used as fallback in text measurer & others
  cOffscreenCanvasDefaultCanvasID = 1;

type

  TCanvasColorComponent = word; // one of R G B A
  TCanvasColor = longint;
  TCanvasLineWidth = TFresnelFloat;
  TCanvasLineCap = byte;
  TCanvasLineJoin = byte;
  TCanvasTextBaseLine = byte;
  TCanvasLineMiterLimit = TFresnelFloat;
  TMenuID = longint;


  TWindowMessageID = longint;
  TWindowMessageParam = longint;
  TWindowMessageData = array [0..WindowMsgSize - 1] of TWindowMessageParam;

  TCanvasMeasureTextParam = TFresnelFloat;
  TCanvasMeasureTextData = array [0..CanvasMeasureTextSize] of TCanvasMeasureTextParam;

  TLineDashPattern = TFresnelFloat;
  TLineDashPatternData = array of TLineDashPattern;

  {$IFDEF PAS2JS}
  UTF8String = String;
  {$ENDIF}

  TTimerID = longint;
  TCanvasRoundRectData = array[0..11] of TFresnelFloat;

  TFresnelDesktopEventCallback = reference to procedure(
    aMessageID: TWindowMessageID;
    const aData: TWindowMessageData
    );

  { TGradientColorPoint }

  TGradientColorPoint = record
    Red, Green, Blue, Alpha: longint;
    Percentage: longint; // Scaled 100
    function ToString: string;
  end;
  TGradientColorPoints = array of TGradientColorPoint;

  { TWindowMessageDataHelper }

  TWindowMessageDataHelper = type helper for TWindowMessageData
    function ToString: utf8string;
  end;

  {$IFNDEF PAS2JS}
  PFresnelFloat = ^TFresnelFloat;
  PWindowCanvasID = ^TWindowCanvasID;
  POffscreenCanvasID = ^TOffscreenCanvasID;
  PCanvasColor = ^TCanvasColor;
  PWindowMessageID = ^TWindowMessageID;
  PWindowMessageData = ^TWindowMessageData;
  PCanvasMeasureTextData = ^TCanvasMeasureTextData;
  PCanvasRoundRectData = ^TCanvasRoundRectData;
  PGradientColorPoints = ^TGradientColorPoint;
  PLineDashPatternData = ^TLineDashPattern;
  PKeyMap = PLongint;
  TWasmPointer = Pointer;
  PMenuID = ^TMenuID;
  {$ELSE}
  TWasmPointer = Longint;
  PFresnelFloat = TWasmPointer;
  PCanvasID = TWasmPointer;
  PCanvasColor = TWasmPointer;
  PCanvasMessageID = TWasmPointer;
  PCanvasMessageData = TWasmPointer;
  PCanvasMeasureTextData = TWasmPointer;
  PCanvasRoundRectData = TWasmPointer;
  PGradientColorPoints = TWasmPointer;
  PLineDashPatternData = TWasmPointer;
  PKeyMap = TWasmPointer;
  PMenuID = TWasmPointer;
  {$ENDIF}

const
  ECANVAS_SUCCESS = 0;
  ECANVAS_NOWINDOW = 10;
  ECANVAS_NOCANVAS = 11;
  ECANVAS_NOIMAGEBITMAP = 12;
  ECANVAS_NOVIDEO = 13;
  ECANVAS_INVALIDPATH = 20;
  ECANVAS_INVALIDPARAM = 21;
  ECANVAS_NOMENUSUPPORT = 22;
  ECANVAS_BUFFERSIZE    = 23;
  ECANVAS_OFFSCREEN     = 30;
  ECANVAS_EXCEPTION     = 40;
  ECANVAS_UNSPECIFIED     = -1;
  ECANVAS_NOFILESELECTION     = -2;
  ECANVAS_INVALIDSELECTEDFILE = -3;
  ECANVAS_INVALIDDESTFILE     = -4;
  ECANVAS_NOFILESUPPORT = -5;
  ECANVAS_WRITEFAILED = -6;

  CANVAS_LINECAP_BUTT = 0;
  CANVAS_LINECAP_ROUND = 1;
  CANVAS_LINECAP_SQUARE = 2;

  CANVAS_LINEJOIN_ROUND = 0;
  CANVAS_LINEJOIN_BEVEL = 1;
  CANVAS_LINEJOIN_MITER = 2;

  EWASMEVENT_SUCCESS = 0;
  EWASMEVENT_NOEVENT = 1;
  EWASMEVENT_NOCANVAS = 2;
  EWASMEVENT_BUFFER_SIZE = 3;
  EWASMEVENT_TRY_AGAIN = 4;
  EWASMEVENT_ERROR = 5;

  // Key state, Based on TShiftStateEnum
  WASM_KEYSTATE_SHIFT = 1 shl Ord(ssShift);
  WASM_KEYSTATE_CTRL = 1 shl Ord(ssAlt);
  WASM_KEYSTATE_ALT = 1 shl Ord(ssCtrl);
  WASM_KEYSTATE_LEFT = 1 shl Ord(ssLeft);
  WASM_KEYSTATE_RIGHT = 1 shl Ord(ssRight);
  WASM_KEYSTATE_MIDDLE = 1 shl Ord(ssMiddle);
  WASM_KEYSTATE_DOUBLE = 1 shl Ord(ssDouble);
  WASM_KEYSTATE_META = 1 shl Ord(ssMeta);
  WASM_KEYSTATE_SUPER = 1 shl Ord(ssSuper);
  WASM_KEYSTATE_HYPER = 1 shl Ord(ssHyper);
  WASM_KEYSTATE_ALTGR = 1 shl Ord(ssAltGr);

  // Location of mouse state data in parameters array
  WASMSG_MOUSESTATE_X = 0;
  WASMSG_MOUSESTATE_Y = 1;
  WASMSG_MOUSESTATE_STATE = 2;
  WASMSG_MOUSESTATE_BUTTON = 3;
  WASMSG_MOUSESTATE_DISTANCE = 3;

  // location of key state data in parameters array
  WASMSG_KEYSTATE_KEYCODE = 0;
  // WASMSG_KEYSTATE_KIND tells how to interpret the WASMSG_KEYSTATE_KEYCODE field.
  WASMSG_KEYSTATE_KIND = 1;
  WASMSG_KEYSTATE_SHIFTSTATE = 2;

  WASMSG_KEYKIND_CHAR              = 0;  // normal character key
  WASMSG_KEYKIND_SPECIAL           = 1;  // Special key: use keycode
  WASMSG_KEYKIND_SPECIAL_SCANCODE  = 1;  // Special key: use keycode
  WASMSG_KEYKIND_SPECIAL_MAP       = 2;  // Special key: use map to map to numerical key code

  // Location of data in measuretext array
  WASMMEASURE_WIDTH = 0;
  WASMMEASURE_HEIGHT = 1;
  WASMMEASURE_ASCENDER = 2;
  WASMMEASURE_DESCENDER = 3;
  // How far the drawn letters themselves reach above and below the baseline.
  // Unlike the two above, these depend on the text: measuring 'x' gives the
  // x-height, measuring 'H' gives the height of a capital letter.
  WASMMEASURE_ACTUALASCENDER = 4;
  WASMMEASURE_ACTUALDESCENDER = 5;

const
  WASMSG_NONE = 0;
  WASMSG_MOVE = 1; // Params[0]= X, [1]=Y, [2]=State
  WASMSG_MOUSEDOWN = 2; // Params[0]= X, [1]=Y, [2]=State
  WASMSG_MOUSEUP = 3; // Params[0]= X, [1]=Y, [2]=State
  WASMSG_MOUSESCROLL = 4; // Params[0]= X, [1]=Y, [2]=State
  WASMSG_CLICK = 5; // Params[0]= X, [1]=Y, [2]=State
  WASMSG_WHEELY = 6; // Params[0]= X, [1]=Y, [2]=State [3]=Distance
  WASMSG_DBLCLICK = 7; // Params[0]= X, [1]=Y, [2]=State
  WASMSG_ENTER = 8;
  WASMSG_LEAVE = 9;
  WASMSG_KEYDOWN = 10;
  WASMSG_KEYUP = 11;
  WASMSG_ACTIVATE = 12;
  WASMSG_DEACTIVATE = 13;
  WASMSG_RESIZE = 14;
  // Params[0]= X, [1]=Y, [2]=PixelRatio*1000 (XY in virtual pixels)
  WASMSG_MOVED = 15; // The window was moved on the desktop. Params[0]= Left, [1]= Top (virtual pixels)

  // brodcast messages are sent from this ID up,
  // they are dispatched to all windows from topmost down
  // if one window handles it, broadcasting stops there
  WASMSG_BROADCAST = 1000;

  // Desktop events (global, WindowID = 0)
  WASMSG_DESKTOP_VISIBILITY = 2001; // Params[0]= visible (0=hidden, 1=visible)
  // Device Manager event
  WASMSG_DEVICEMANAGER_DEVICE_CHANGED = 2002;
  // Params[0]= disconnected (0=disconnected, 1=connected)

  WASMSG_DEVICEMANAGER_GETUSERMEDIA_ERROR = 0;
  WASMSG_DEVICEMANAGER_GETUSERMEDIA_REFUSED_BY_USER = 1;
  WASMSG_DEVICEMANAGER_DEVICE_STATE_CHANGED = 2;
  WASMSG_DEVICEMANAGER_DEVICE_STATE_CONNECTED = 3;
  WASMSG_DEVICEMANAGER_DEVICE_STATE_DISCONNECTED = 4;

  // Roundrect flags
  ROUNDRECT_FLAG_FILL = 1;

  // Indexes for roundrect data array.

  ROUNDRECT_BOXTOPLEFTX = 0;
  ROUNDRECT_BOXTOPLEFTY = 1;
  ROUNDRECT_BOXBOTTOMRIGHTX = 2;
  ROUNDRECT_BOXBOTTOMRIGHTY = 3;
  ROUNDRECT_RADIITOPLEFTX = 4;
  ROUNDRECT_RADIITOPLEFTY = 5;
  ROUNDRECT_RADIITOPRIGHTX = 6;
  ROUNDRECT_RADIITOPRIGHTY = 7;
  ROUNDRECT_RADIIBOTTOMLEFTX = 8;
  ROUNDRECT_RADIIBOTTOMLEFTY = 9;
  ROUNDRECT_RADIIBOTTOMRIGHTX = 10;
  ROUNDRECT_RADIIBOTTOMRIGHTY = 11;

  // Flags for SetImageFillStyle
  IMAGEFILLSTYLE_NOREPEAT = 0;
  IMAGEFILLSTYLE_REPEAT = 1;
  IMAGEFILLSTYLE_REPEATX = 2;
  IMAGEFILLSTYLE_REPEATY = 3;

  DRAWIMAGE_DESTX = 0;
  DRAWIMAGE_DESTY = 1;
  DRAWIMAGE_DESTWIDTH = 2;
  DRAWIMAGE_DESTHEIGHT = 3;
  DRAWIMAGE_SRCX = 4;
  DRAWIMAGE_SRCY = 5;
  DRAWIMAGE_SRCWIDTH = 6;
  DRAWIMAGE_SRCHEIGHT = 7;
  DRAWIMAGE_IMAGEWIDTH = 8;
  DRAWIMAGE_IMAGEHEIGHT = 9;

  // Flags for Arc
  ARC_FILL = 1;
  ARC_ROTATE = 2;

  // Flags for DrawPath
  DRAWPATH_CLOSEPATH = 1;
  DRAWPATH_FILLPATH = 2;
  DRAWPATH_STROKEPATH = 4;

  // Point types in DrawPath
  DRAWPATH_TYPEMOVETO = 0;
  DRAWPATH_TYPELINETO = 1;
  DRAWPATH_TYPECURVETO = 2;
  DRAWPATH_TYPECLOSE = 3;

  // Which window size ?
  GETVIEWPORTSIZE_CLIENT = 0;
  GETVIEWPORTSIZE_WINDOW = 1;

  // Baseline for text
  TEXTBASELINE_TOP = 0;
  TEXTBASELINE_HANGING = 1;
  TEXTBASELINE_MIDDLE = 2;
  TEXTBASELINE_ALPHABETIC = 3;
  TEXTBASELINE_IDEOGRAPHIC = 4;
  TEXTBASELINE_BOTTOM = 5;

  // Menu flags
  MENU_FLAGS_INVISIBLE = 1;
  MENU_FLAGS_CHECKED = 2;
  MENU_FLAGS_RADIO = 4;

  // User Media flags
  cUserMediaCaptureOption_RequestAnimationFrame = 1;
  cUserMediaCaptureOption_SelfieSegmentationBlur = 2;

  // Display Media flags
  cDisplayMediaCaptureOption_RequestAnimationFrame = 1;
  cDisplayMediaCaptureOption_IncludeAudio = 2;
  cDisplayMediaCaptureOption_ShowCursor = 4;

type
  TFresnelDisplayMediaKind = (
    fdmkUnknown,
    fdmkScreen,
    fdmkWindow,
    fdmkTab,
    fdmkBrowser
    );

function LineCapToString(aCap: TCanvasLineCap): string;
function LineJoinToString(aJoin: TCanvasLineJoin): string;
function TextBaseLineToString(aBaseLine: TCanvasTextBaseLine): string;

{
Function FresnelUnScale(aLen : Longint) : TFresnelFloat;
Function FresnelScale(aLen : TFresnelFloat) : Longint;
}

implementation

{
Function FresnelUnScale(aLen : Longint) : TFresnelFloat;

begin
  Result:=aLen/FresnelScaleFactor;
end;

Function FresnelScale(aLen : TFresnelFloat) : Longint;

begin
  Result:=Round(aLen*FresnelScaleFactor);
end;
}

function LineCapToString(aCap: TCanvasLineCap): string;
begin
  case aCap of
    CANVAS_LINECAP_BUTT: Result := 'butt';
    CANVAS_LINECAP_ROUND: Result := 'round';
    CANVAS_LINECAP_SQUARE: Result := 'square';
    else
      Result := 'butt';
  end;
end;

function LineJoinToString(aJoin: TCanvasLineJoin): string;
begin
  case aJoin of
    CANVAS_LINEJOIN_ROUND: Result := 'round';
    CANVAS_LINEJOIN_MITER: Result := 'miter';
    CANVAS_LINEJOIN_BEVEL: Result := 'bevel';
    else
      Result := 'round';
  end;
end;

function TextBaseLineToString(aBaseLine: TCanvasTextBaseLine): string;
begin
  case aBaseLine of
    TEXTBASELINE_TOP: Result := 'top';
    TEXTBASELINE_HANGING: Result := 'hanging';
    TEXTBASELINE_MIDDLE: Result := 'middle';
    TEXTBASELINE_ALPHABETIC: Result := 'alphabetic';
    TEXTBASELINE_IDEOGRAPHIC: Result := 'ideographic';
    TEXTBASELINE_BOTTOM: Result := 'bottom';
    else
      Result := 'alphabetic';
  end;
end;

{ TWindowCanvasIDHelper }

function TWindowCanvasIDHelper.ToIDString: string;
begin
  Result := IntToStr(
    {$ifdef PAS2JS}
Self
    {$else}
    Win
    {$endif}
    );
end;

function TWindowCanvasIDHelper.ToString: string;
begin
  Result := 'Window' + ToIDString;
end;

{ TOffscreenCanvasIDHelper }

function TOffscreenCanvasIDHelper.ToIDString: string;
begin
  Result := IntToStr(
    {$ifdef PAS2JS}
Self
    {$else}
    Cnv
    {$endif}
    );
end;

function TOffscreenCanvasIDHelper.ToString: string;
begin
  Result := 'Canvas' + ToIDString;
end;

{ TVideoElementIDHelper }

function TVideoElementIDHelper.ToIDString: string;
begin
  Result := IntToStr(
    {$ifdef PAS2JS}
Self
    {$else}
    Vid
    {$endif}
    );
end;

function TVideoElementIDHelper.ToString: string;
begin
  Result := 'Video' + ToIDString;
end;

{ TGradientColorPoint }

function TGradientColorPoint.ToString: string;
begin
  Result := string(Format('{%g%% (r:%d, g:%d, b:%d / %d)}',
    [Percentage / 100, Red, Green, Blue, Alpha]));
end;

{ TWindowMessageDataHelper }

function TWindowMessageDataHelper.ToString: utf8string;
var
  I: integer;
  buffer: string;
begin
  buffer := IntToStr(Self[0]);
  for I := 1 to WindowMsgSize - 1 do
  begin
    buffer := buffer + ',';
    buffer := buffer + IntToStr(Self[I]);
  end;
  Result := utf8string('[' + buffer + ']');
end;

end.
